import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ResizableDraggableDialog from 'spw-components/dialogs/resizable-draggable-dialog';
import { Clear } from '@mui/icons-material';
import { Box, IconButton, Typography, Menu, MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import styled from 'styled-components';
import { isNil } from 'ramda';
import {
  selectProjectSpaceTypeCategories,
  projectColors as selectProjectColors,
  selectCurrentUserIsViewOnly,
} from 'spw-redux/modules/projects/selectors';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { ColorDot } from 'spw-components/worksheet/styles';
import { getColor, NON_WORKSPACE_CATEGORIES } from 'spw-shared/utils';
import { subcategoriesRankMap } from 'spw-components/worksheet/utils/hooks';
import CategorySpaceTypeList, {
  MoreButton,
} from 'spw-programming-projects/building-details/worksheet/category-space-type-list';
import { colors } from 'spw-theme';
import { useSpaceTypeOptions } from 'spw-programming-projects/building-details/worksheet/space-type-hooks';
import {
  selectCurrentScenario,
  selectShowWorksheetUpdateDialog,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import HideSpaceTypeConfirmationDialog from 'spw-programming-projects/building-details/worksheet/hide-space-type-confirmation-dialog';
import {
  useDeleteWorksheetRow,
  useDeleteChildRow,
} from 'spw-programming-projects/building-details/worksheet/hooks';
import {
  defaultCategoryAndSubcategory,
  isRowDirty,
} from 'spw-programming-projects/building-details/worksheet/utils';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import VisibilityButton from 'spw-programming-projects/building-details/worksheet/visibility-button';
import {
  selectSelectedChildSpaceType,
  selectSelectedSpaceType,
  selectSelectedSpaceTypeCategory,
} from 'spw-redux/modules/worksheet/selectors';
import {
  addRowsToProjectWorksheets,
  fetchScenariosAssociatedWithSelectedCategory,
  setRemoveSpaceTypeDialogStatus,
  setSelectedChildSpaceType,
  setSelectedSpaceType,
  setSelectedSpaceTypeCategory,
  updateWorksheetCategory,
} from 'spw-redux/modules/worksheet/actions';
import { useCustomSpaceTypes } from 'spw-components/dialogs/custom-space-types/hooks';
import WorksheetSpaceTypeDialog from 'spw-components/dialogs/custom-space-types/worksheet-space-type-dialog';
import { WorksheetCategory } from 'spw-redux/modules/calculators/types';
import { INDIVIDUAL_WORKSPACES_CATEGORY } from 'spw-constants';
import EditCategoryNameDialog from 'spw-components/dialogs/custom-space-types/edit-category-name-dialog';

type Props = {
  selectedCategoryId: number;
  anchorEl: HTMLElement;
  onClose: () => void;
};

const PopoverHeader = styled.div`
  cursor: move;
  display: flex;
  flex-direction: column;
  padding: 16px 24px;

  .title {
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }

  .subtitle {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`;

const Container = styled(Box)`
  height: calc(100% - 86px);

  h5 {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    margin: 0;
  }
`;

const Content = styled(Box)`
  overflow: hidden auto;
  height: calc(100% - 62px);
`;

const DotContainer = styled(Box)`
  display: flex;
  width: 24px;
  height: 24px;
  margin-right: 4px;
  justify-content: center;
  align-items: center;
`;
const SmallDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.color} !important;
`;

const CategoryNameRow = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${colors.white};
`;

const CategoryName = styled(Box)`
  height: 24px;
  display: flex;
`;

const SubcategoryName = styled(Box)`
  padding: 4px 24px 4px 52px;
  margin: 0 -24px;
  display: flex;
  height: 24px;
  align-items: center;
  cursor: default;
  word-break: break-all;
  &:hover {
    background: ${colors.brightGray};
  }
`;

const MIN_POPOVER_HEIGHT = 150;
const MIN_POPOVER_WIDTH = 418;
const DIALOG_OFFSET_LEFT = 428;

const CategoryMenuItem = styled(MenuItem)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const DIALOG_OFFSET_TOP = 50;
const OFFSET_BOTTOM = 5;

const ManageSpaceTypesPopover = ({
  selectedCategoryId,
  anchorEl,
  onClose,
}: Props) => {
  const dispatch = useDispatch();
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const isShowingWorksheetUpdateDialog = useSelector(
    selectShowWorksheetUpdateDialog
  );
  const spaceTypeCategories = useSelector(selectProjectSpaceTypeCategories);
  const projectColors = useSelector(selectProjectColors);
  const scenario = useSelector(selectCurrentScenario);
  const worksheet = useSelector(selectWorksheet);
  const selectedSpaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  );
  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );
  const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);
  const [
    isHideSpaceTypeConfirmationDialogOpen,
    setIsHideSpaceTypeConfirmationDialogOpen,
  ] = useState(false);
  const [isRenameCategoryDialogOpen, setIsRenameCategoryDialogOpen] = useState(
    false
  );

  const selectedSpaceType = useSelector(selectSelectedSpaceType);
  const selectedChildSpaceType = useSelector(selectSelectedChildSpaceType);
  const [selectedCategoryNode, setSelectedCategoryNode] = useState<HTMLElement>(
    null
  );
  const deleteWorksheetRow = useDeleteWorksheetRow();
  const deleteChildWorksheetRow = useDeleteChildRow();
  const categoryRefs: Record<
    number,
    React.MutableRefObject<HTMLDivElement>
  > = useMemo(
    () =>
      spaceTypeCategories?.reduce(
        (map, projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
          return {
            ...map,
            [projectSpaceTypeCategory.id]: React.createRef(),
          };
        },
        {}
      ),
    [spaceTypeCategories]
  );

  const subcategoryRefs: Record<
    number,
    React.MutableRefObject<HTMLDivElement>
  > = useMemo(
    () =>
      spaceTypeCategories
        ?.reduce(
          (allSubcats, projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
            return [
              ...allSubcats,
              ...(projectSpaceTypeCategory.subcategories || []),
            ];
          },
          []
        )
        .reduce((subcatMap, subcategory: IProjectSpaceTypeSubcategory) => {
          return {
            ...subcatMap,
            [subcategory.id]: React.createRef(),
          };
        }, {}),
    [spaceTypeCategories]
  );

  const processRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      setSelectedCategoryNode(node);
    }
  }, []);

  const worksheetCategoryVisibility: Record<
    number,
    WorksheetCategory
  > = worksheet?.categories?.reduce((map, worksheetCategory) => {
    return {
      ...map,
      [worksheetCategory.categoryId]: worksheetCategory.visible,
    };
  }, {});

  const {
    isCreateCustomSpaceTypeDialogOpen,
    setIsCreateCustomSpaceTypeDialogOpen,
    handleCreateCustomSpaceType,
    selectedParentSpaceType,
    setSelectedParentSpaceType,
    isEditSpaceTypeNameDialogOpen,
    setIsEditSpaceTypeNameDialogOpen,
  } = useSpaceTypeOptions();

  const onDismissDialog = () => {
    setIsCreateCustomSpaceTypeDialogOpen(false);
    setIsEditSpaceTypeNameDialogOpen(false);
    setIsRenameCategoryDialogOpen(false);
  };

  const { selectParentSpaceType } = useCustomSpaceTypes({
    setSelectedParentSpaceType,
  });

  const hideCategoryRows = useCallback(
    (
      spaceTypeCategory:
        | IProjectSpaceTypeCategory
        | IProjectSpaceTypeSubcategory
    ) => {
      dispatch(
        updateWorksheetCategory(
          worksheet.id,
          spaceTypeCategory.id,
          false,
          spaceTypeCategory.parentId
        )
      );
    },
    [dispatch, worksheet]
  );

  const handleMoreButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    spaceTypeCategory: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory
  ) => {
    event.stopPropagation();
    setCategoryAnchorEl(event.currentTarget);
    dispatch(setRemoveSpaceTypeDialogStatus(false));
    dispatch(setSelectedSpaceTypeCategory(spaceTypeCategory));
    dispatch(
      fetchScenariosAssociatedWithSelectedCategory(spaceTypeCategory.id)
    );
  };

  const handleOnCloseMenu = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    setCategoryAnchorEl(null);
  };

  const initialHeight = useMemo(() => {
    return window.innerHeight * 0.65;
  }, []);

  const handleHideSpaceTypeConfirm = useCallback(() => {
    setIsHideSpaceTypeConfirmationDialogOpen(false);
    if (selectedSpaceTypeCategory) {
      hideCategoryRows(selectedSpaceTypeCategory);
      dispatch(setSelectedSpaceTypeCategory(null));
    } else if (selectedChildSpaceType) {
      deleteChildWorksheetRow(selectedChildSpaceType);
      dispatch(setSelectedChildSpaceType(null));
    } else {
      deleteWorksheetRow(selectedSpaceType);
      dispatch(setSelectedSpaceType(null));
    }
  }, [
    dispatch,
    deleteWorksheetRow,
    deleteChildWorksheetRow,
    selectedSpaceTypeCategory,
    selectedSpaceType,
    selectedChildSpaceType,
    hideCategoryRows,
  ]);

  const handleHideSpaceTypeClose = () => {
    setIsHideSpaceTypeConfirmationDialogOpen(false);
    dispatch(setSelectedSpaceType(null));
    dispatch(setSelectedChildSpaceType(null));
    dispatch(setSelectedSpaceTypeCategory(null));
  };

  const handleShowCategoryClick = useCallback(
    (
      spaceTypeCategory:
        | IProjectSpaceTypeCategory
        | IProjectSpaceTypeSubcategory
    ) => {
      dispatch(
        updateWorksheetCategory(
          worksheet.id,
          spaceTypeCategory.id,
          true,
          spaceTypeCategory.parentId
        )
      );
    },
    [dispatch, worksheet]
  );

  const handleHideCategoryClick = (
    spaceTypeCategory: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory
  ) => {
    const defaultCategory = spaceTypeCategory.defaultSpaceTypeCategory.category;

    const rows = spaceTypeCategory.parentId
      ? worksheet.rowsBySubcategory[defaultCategory]
      : worksheet.rowsByCategory[defaultCategory];

    if ((rows || []).some(isRowDirty)) {
      dispatch(setSelectedSpaceTypeCategory(spaceTypeCategory));
      setIsHideSpaceTypeConfirmationDialogOpen(true);
    } else {
      hideCategoryRows(spaceTypeCategory);
    }
  };

  const scrollToCategory = useCallback(
    (spaceTypeCategoryId: number) => {
      let categoryNode;

      if (spaceTypeCategoryId === selectedCategoryId && selectedCategoryNode) {
        categoryNode = selectedCategoryNode;
      } else if (categoryRefs[spaceTypeCategoryId].current) {
        categoryNode = categoryRefs[spaceTypeCategoryId].current;
      }

      if (categoryNode) {
        categoryNode.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [selectedCategoryId, selectedCategoryNode, categoryRefs]
  );

  const scrollToNewlyCreatedSpaceType = useCallback(
    (categoryId: number) => {
      const ref =
        subcategoryRefs[categoryId]?.current ||
        categoryRefs[categoryId]?.current;
      if (ref) {
        ref.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    },
    [subcategoryRefs, categoryRefs]
  );

  const handleCreateCustomSpaceTypeOption = () => {
    handleCreateCustomSpaceType();
    setCategoryAnchorEl(null);

    const { category, subcategory } = defaultCategoryAndSubcategory(
      selectedSpaceTypeCategory,
      spaceTypeCategories
    );
    selectParentSpaceType(category, subcategory);
  };

  const handleRenameCategoryClick = () => {
    setIsRenameCategoryDialogOpen(true);
    const { category, subcategory } = defaultCategoryAndSubcategory(
      selectedSpaceTypeCategory,
      spaceTypeCategories
    );
    selectParentSpaceType(category, subcategory);
    setCategoryAnchorEl(null);
  };

  const handleHideFromAllWorksheets = () => {
    dispatch(setRemoveSpaceTypeDialogStatus(true, true));
    setCategoryAnchorEl(null);
  };

  const handleShowOnAllWorksheets = () => {
    setCategoryAnchorEl(null);
    dispatch(
      addRowsToProjectWorksheets({
        spaceTypeCategoryId: selectedSpaceTypeCategory.id,
      })
    );
  };

  const initialPosition = useMemo(() => {
    if (!anchorEl) {
      return { top: 0, left: 0 };
    }

    const { top, left } = anchorEl.getBoundingClientRect();

    const leftPos = left - DIALOG_OFFSET_LEFT;

    if (top - DIALOG_OFFSET_TOP + initialHeight > window.innerHeight) {
      return {
        top: window.innerHeight - initialHeight - OFFSET_BOTTOM,
        left: leftPos > 0 ? leftPos : 0,
      };
    }
    return {
      top: top - DIALOG_OFFSET_TOP,
      left: leftPos > 0 ? leftPos : 0,
    };
  }, [anchorEl, initialHeight]);

  useEffect(() => {
    if (selectedCategoryNode) {
      scrollToCategory(selectedCategoryId);
    }
  }, [selectedCategoryNode, selectedCategoryId, scrollToCategory]);

  const categories = useMemo(() => {
    if (scenario.isShared) {
      return spaceTypeCategories.filter(
        (stc) =>
          stc.defaultSpaceTypeCategory.category ===
          INDIVIDUAL_WORKSPACES_CATEGORY
      );
    }
    return spaceTypeCategories;
  }, [scenario, spaceTypeCategories]);

  useEffect(() => {
    if (isShowingWorksheetUpdateDialog) onClose();
  }, [isShowingWorksheetUpdateDialog, onClose]);

  if (!anchorEl) return null;

  return (
    <ResizableDraggableDialog
      anchorEl={anchorEl}
      open={!isNil(anchorEl)}
      minWidth={MIN_POPOVER_WIDTH}
      minHeight={MIN_POPOVER_HEIGHT}
      height={initialHeight}
      defaultPosition={initialPosition}
      enableConfig={{ left: false, topLeft: false, bottomLeft: false }}
      dialogPaperStyle={{
        zIndex: 1300,
      }}
      title={
        <PopoverHeader>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <div className="title">Manage Space Types</div>
            <IconButton
              data-cy="close-manage-space-types-dialog"
              onClick={onClose}
              style={{ padding: 0 }}
            >
              <Clear data-cy="close-popover-icon" />
            </IconButton>
          </Box>
          <Box>
            <div className="subtitle">
              {clientFacingNamesAreVisible ? 'Client Names' : 'Internal Names'}
            </div>
          </Box>
        </PopoverHeader>
      }
      draggable
    >
      <Container>
        {categories.length > 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            pb={1}
            pt={1}
            pl={3}
            pr={3}
            maxWidth={254}
          >
            {categories.map((spaceTypeCategory: IProjectSpaceTypeCategory) => {
              return (
                <ColorDot
                  key={`dot-${spaceTypeCategory.id}`}
                  onClick={() => scrollToCategory(spaceTypeCategory.id)}
                  color={getColor(
                    spaceTypeCategory.defaultSpaceTypeCategory.category,
                    projectColors
                  )}
                  className="large-button"
                >
                  <img
                    src={`/images/${spaceTypeCategory.icon}.svg`}
                    alt={`${spaceTypeCategory.name}-icon`}
                  />
                </ColorDot>
              );
            })}
          </Box>
        )}
        <Content>
          <Box>
            {categories.map(
              (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
                const subcategories = (
                  projectSpaceTypeCategory.subcategories || []
                ).sort(
                  (
                    subcategoryA: IProjectSpaceTypeCategory,
                    subcategoryB: IProjectSpaceTypeCategory
                  ) => {
                    return (
                      subcategoriesRankMap[
                        subcategoryA.defaultSpaceTypeCategory.category
                      ] -
                      subcategoriesRankMap[
                        subcategoryB.defaultSpaceTypeCategory.category
                      ]
                    );
                  }
                );

                return (
                  <div
                    key={projectSpaceTypeCategory.id}
                    ref={
                      projectSpaceTypeCategory.id === selectedCategoryId
                        ? processRef
                        : categoryRefs[projectSpaceTypeCategory.id]
                    }
                  >
                    <CategoryNameRow pl={3} pr={3}>
                      <CategoryName pb={1} pt={1}>
                        <Box display="flex" alignItems="center" flexGrow={1}>
                          <DotContainer>
                            <SmallDot
                              color={getColor(
                                projectSpaceTypeCategory
                                  .defaultSpaceTypeCategory.category,
                                projectColors
                              )}
                            />
                          </DotContainer>

                          <Typography
                            variant="h5"
                            pt={0.25}
                            style={
                              !worksheetCategoryVisibility[
                                projectSpaceTypeCategory.id
                              ]
                                ? { color: colors.disabledIcons }
                                : {}
                            }
                          >
                            {projectSpaceTypeCategory.name.toUpperCase()}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <VisibilityButton
                            isOff={
                              !worksheetCategoryVisibility[
                                projectSpaceTypeCategory.id
                              ]
                            }
                            onClick={() => {
                              if (
                                worksheetCategoryVisibility[
                                  projectSpaceTypeCategory.id
                                ]
                              ) {
                                handleHideCategoryClick(
                                  projectSpaceTypeCategory
                                );
                              } else {
                                handleShowCategoryClick(
                                  projectSpaceTypeCategory
                                );
                              }
                            }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <MoreButton
                            disabled={currentUserIsViewOnly}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              handleMoreButtonClick(
                                event,
                                projectSpaceTypeCategory
                              );
                            }}
                          />
                        </Box>
                      </CategoryName>
                      <Divider />
                    </CategoryNameRow>
                    <Box>
                      {subcategories.map((subcategory) => {
                        return (
                          <Box
                            pl={3}
                            pr={3}
                            key={subcategory.id}
                            ref={(node: HTMLDivElement) => {
                              if (subcategory.id)
                                subcategoryRefs[subcategory.id].current = node;
                            }}
                          >
                            <SubcategoryName>
                              <Box
                                display="flex"
                                flexGrow={1}
                                alignItems="center"
                                data-cy={`${subcategory.name
                                  .replace(' ', '_')
                                  .trim()}_menu_item`}
                                style={
                                  !worksheetCategoryVisibility[subcategory.id]
                                    ? { color: colors.disabledIcons }
                                    : {}
                                }
                              >
                                <Typography variant="h5">
                                  {subcategory.name.toUpperCase()}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center">
                                <VisibilityButton
                                  isOff={
                                    !worksheetCategoryVisibility[subcategory.id]
                                  }
                                  onClick={() => {
                                    if (
                                      worksheetCategoryVisibility[
                                        subcategory.id
                                      ]
                                    ) {
                                      handleHideCategoryClick(subcategory);
                                    } else {
                                      handleShowCategoryClick(subcategory);
                                    }
                                  }}
                                />
                              </Box>
                              <Box>
                                <MoreButton
                                  onClick={(
                                    event: React.MouseEvent<HTMLButtonElement>
                                  ) => {
                                    handleMoreButtonClick(event, subcategory);
                                  }}
                                />
                              </Box>
                            </SubcategoryName>
                            <Box
                              style={{
                                paddingLeft: scenario.isDetailed
                                  ? '0px'
                                  : '28px',
                              }}
                            >
                              <CategorySpaceTypeList
                                selectParentSpaceType={selectParentSpaceType}
                                category={subcategory}
                                setIsHideSpaceTypeConfirmationDialogOpen={
                                  setIsHideSpaceTypeConfirmationDialogOpen
                                }
                                setIsEditSpaceTypeNameDialogOpen={
                                  setIsEditSpaceTypeNameDialogOpen
                                }
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    <Box pl="52px" pr={3}>
                      <CategorySpaceTypeList
                        selectParentSpaceType={selectParentSpaceType}
                        category={projectSpaceTypeCategory}
                        setIsHideSpaceTypeConfirmationDialogOpen={
                          setIsHideSpaceTypeConfirmationDialogOpen
                        }
                        setIsEditSpaceTypeNameDialogOpen={
                          setIsEditSpaceTypeNameDialogOpen
                        }
                      />
                    </Box>
                  </div>
                );
              }
            )}
          </Box>
        </Content>
        <Menu
          anchorEl={categoryAnchorEl}
          open={Boolean(categoryAnchorEl)}
          onClose={handleOnCloseMenu}
          disableScrollLock
        >
          <CategoryMenuItem onClick={handleRenameCategoryClick}>
            Rename{' '}
            {selectedSpaceTypeCategory?.parentId
              ? 'Subcategory'
              : 'Space Category'}
          </CategoryMenuItem>

          {(selectedSpaceTypeCategory?.parentId ||
            NON_WORKSPACE_CATEGORIES.includes(
              selectedSpaceTypeCategory?.defaultSpaceTypeCategory?.category
            )) && (
            <CategoryMenuItem onClick={handleCreateCustomSpaceTypeOption}>
              Create Custom Space Type
            </CategoryMenuItem>
          )}

          {worksheetCategoryVisibility[selectedSpaceTypeCategory?.id] ? (
            <CategoryMenuItem onClick={handleHideFromAllWorksheets}>
              Hide on All Worksheets
            </CategoryMenuItem>
          ) : (
            <CategoryMenuItem onClick={handleShowOnAllWorksheets}>
              Show on All Worksheets
            </CategoryMenuItem>
          )}
        </Menu>
        {(isCreateCustomSpaceTypeDialogOpen ||
          isEditSpaceTypeNameDialogOpen) && (
          <WorksheetSpaceTypeDialog
            isOpen={
              isCreateCustomSpaceTypeDialogOpen || isEditSpaceTypeNameDialogOpen
            }
            onDismiss={onDismissDialog}
            isEditingSpaceType={isEditSpaceTypeNameDialogOpen}
            parentSpaceType={selectedParentSpaceType}
            onCreate={({ id }) => {
              setTimeout(() => scrollToNewlyCreatedSpaceType(id), 100);
            }}
          />
        )}
        {isRenameCategoryDialogOpen && (
          <EditCategoryNameDialog
            isOpen={isRenameCategoryDialogOpen}
            onDismiss={onDismissDialog}
          />
        )}

        <HideSpaceTypeConfirmationDialog
          open={isHideSpaceTypeConfirmationDialogOpen}
          projectSpaceType={selectedSpaceType}
          childSpaceType={selectedChildSpaceType}
          onConfirm={handleHideSpaceTypeConfirm}
          onClose={handleHideSpaceTypeClose}
        />
      </Container>
    </ResizableDraggableDialog>
  );
};

export default ManageSpaceTypesPopover;
